import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Crown, Check, TrendingUp, Users, Percent, Wallet } from 'lucide-react';
import { packageApi, investmentApi, userApi, settingsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function UserPackages() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [amount, setAmount] = useState('');
  const [walletSource, setWalletSource] = useState('deposit_wallet');
  const [submitting, setSubmitting] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, investmentsRes, dashboardRes, settingsRes] = await Promise.all([
        packageApi.getActive(),
        investmentApi.getAll(),
        userApi.getDashboard(),
        settingsApi.get()
      ]);
      setPackages(packagesRes.data.packages);
      setInvestments(investmentsRes.data.investments);
      setDashboard(dashboardRes.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    if (!selectedPackage || !amount) return;

    const amountNum = parseFloat(amount);
    if (amountNum < selectedPackage.min_amount || amountNum > selectedPackage.max_amount) {
      toast.error(`Amount must be between $${selectedPackage.min_amount} and $${selectedPackage.max_amount}`);
      return;
    }

    // Check selected wallet balance
    const selectedBalance = walletSource === 'deposit_wallet' 
      ? (dashboard?.deposit_wallet || 0) 
      : (dashboard?.cash_wallet || 0);
    
    if (amountNum > selectedBalance) {
      const walletName = walletSource === 'deposit_wallet' ? 'Deposit Wallet' : 'Cash Wallet';
      toast.error(`Insufficient balance in ${walletName}. Available: $${selectedBalance.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      await investmentApi.create({
        package_id: selectedPackage.id,
        amount: amountNum,
        wallet_source: walletSource
      });
      toast.success('Participation activated successfully!');
      setSelectedPackage(null);
      setAmount('');
      setWalletSource('deposit_wallet');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate participation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const depositBalance = dashboard?.deposit_wallet || 0;
  const cashBalance = dashboard?.cash_wallet || 0;
  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="space-y-8" data-testid="packages-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-amber-500/10">
          <Package className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Staking Packages</h1>
          <p className="text-sm text-zinc-500">Choose a tier to start earning rewards</p>
        </div>
      </div>

      {/* Balance Info */}
      <div className="glass-card p-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-black/30">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-amber-400" />
              <p className="text-xs text-zinc-500">Deposit Wallet</p>
            </div>
            <p className="text-xl text-amber-400 font-medium">${depositBalance.toFixed(2)}</p>
            <p className="text-[10px] text-amber-400/70">{usdToBLC(depositBalance)} BLC</p>
            <p className="text-[10px] text-zinc-400">From deposits - available for staking</p>
          </div>
          <div className="p-3 rounded-lg bg-black/30">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-green-400" />
              <p className="text-xs text-zinc-500">Cash Wallet</p>
            </div>
            <p className="text-xl text-green-400 font-medium">${cashBalance.toFixed(2)}</p>
            <p className="text-[10px] text-green-400/70">{usdToBLC(cashBalance)} BLC</p>
            <p className="text-[10px] text-zinc-400">From earnings - stake anytime or withdraw on allowed dates</p>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-200 text-center">
            <strong>Tip:</strong> Cash Wallet funds can be used for staking instantly without waiting for withdrawal dates!
          </p>
        </div>
      </div>

      {/* Packages Grid */}
      {packages.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-white font-medium text-lg mb-2">No Packages Available</h3>
          <p className="text-zinc-500">Packages will be available soon. Contact admin for more info.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg, idx) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`package-card ${idx === 1 ? 'featured' : ''}`}
            >
              {idx === 1 && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-yellow-600 text-black text-xs font-bold text-center py-1">
                  MOST POPULAR
                </div>
              )}
              <div className={`p-6 ${idx === 1 ? 'pt-8' : ''}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium text-lg">{pkg.name}</h3>
                    <p className="text-xs text-zinc-500">{pkg.duration_days} Days Duration</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Participation Range</span>
                    <span className="text-white font-medium">
                      ${pkg.min_amount} - ${pkg.max_amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Daily Reward</span>
                    <span className="text-green-400 font-medium">{pkg.daily_roi}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Annual Reward</span>
                    <span className="text-amber-400 font-medium">{pkg.annual_roi?.toFixed(1) || (pkg.daily_roi * 365).toFixed(1)}%</span>
                  </div>
                </div>

                {/* Commission Rates Section */}
                <div className="border-t border-white/10 pt-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Percent className="w-4 h-4 text-amber-400" />
                    <p className="text-xs text-zinc-400">Referral Commission Rates</p>
                  </div>
                  
                  {/* Direct Bonus */}
                  <div className="flex justify-between items-center mb-2 p-2 rounded bg-green-500/10">
                    <span className="text-xs text-green-400 font-medium">Direct Bonus</span>
                    <span className="text-green-400 font-bold">{pkg.commission_rates?.direct || 0}%</span>
                  </div>
                  
                  {/* Level Bonuses */}
                  <div className="grid grid-cols-5 gap-1">
                    {pkg.commission_levels?.map((enabled, i) => (
                      <div 
                        key={i}
                        className={`p-2 rounded text-center ${
                          enabled ? 'bg-blue-500/10' : 'bg-zinc-800/50'
                        }`}
                      >
                        <p className={`text-[10px] ${enabled ? 'text-blue-400' : 'text-zinc-600'}`}>
                          L{i + 1}
                        </p>
                        <p className={`text-xs font-bold ${enabled ? 'text-white' : 'text-zinc-600'}`}>
                          {enabled ? `${pkg.commission_rates?.[`level${i + 1}`] || (i === 0 ? pkg.commission_rates?.direct : 0)}%` : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setSelectedPackage(pkg);
                    setAmount(pkg.min_amount.toString());
                  }}
                  className="w-full gold-btn"
                  data-testid={`stake-${pkg.name.toLowerCase().replace(' ', '-')}`}
                >
                  Stake Now
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Active Investments */}
      {investments.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="text-white font-medium mb-4">Your Active Participations</h2>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Amount</th>
                  <th>Daily Reward</th>
                  <th>Total Earned</th>
                  <th>Days Left</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {investments.map((inv) => (
                  <tr key={inv.id}>
                    <td className="text-white font-medium">{inv.package_name}</td>
                    <td className="text-white">${inv.amount.toFixed(2)} <span className="text-amber-400 text-xs ml-1">({usdToBLC(inv.amount)} BLC)</span></td>
                    <td className="text-green-400">{inv.daily_roi}%</td>
                    <td className="text-amber-400">${inv.total_earned.toFixed(2)} <span className="text-amber-400/70 text-xs ml-1">({usdToBLC(inv.total_earned)} BLC)</span></td>
                    <td className="text-zinc-400">{inv.days_remaining}</td>
                    <td>
                      <span className={`badge ${inv.is_active ? 'badge-success' : 'badge-info'}`}>
                        {inv.is_active ? 'Active' : 'Completed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stake Modal */}
      <Dialog open={!!selectedPackage} onOpenChange={() => setSelectedPackage(null)}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Activate {selectedPackage?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 rounded-lg bg-black/30 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Duration</span>
                <span className="text-white">{selectedPackage?.duration_days} Days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Daily Reward</span>
                <span className="text-green-400">{selectedPackage?.daily_roi}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Range</span>
                <span className="text-white">
                  ${selectedPackage?.min_amount} - ${selectedPackage?.max_amount?.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Commission Structure */}
            {selectedPackage && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Commission Structure</span>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Direct Bonus:</span>
                    <span className="text-white font-medium">{selectedPackage.commission_rates?.direct || 0}%</span>
                  </div>
                  {selectedPackage.commission_levels?.map((enabled, i) => enabled && i > 0 && (
                    <div key={i} className="flex justify-between">
                      <span className="text-zinc-400">Level {i + 1}:</span>
                      <span className="text-white font-medium">{selectedPackage.commission_rates?.[`level${i + 1}`] || 0}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wallet Selection */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Select Wallet Source</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWalletSource('deposit_wallet')}
                  className={`p-3 rounded-lg text-sm font-medium transition-all flex flex-col items-center ${
                    walletSource === 'deposit_wallet' 
                      ? 'bg-amber-500 text-black' 
                      : 'glass text-zinc-400 hover:text-white'
                  }`}
                  data-testid="select-deposit-wallet"
                >
                  <span>Deposit Wallet</span>
                  <span className="text-xs mt-1">${depositBalance.toFixed(2)}</span>
                  <span className="text-[10px] opacity-70">{usdToBLC(depositBalance)} BLC</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWalletSource('cash_wallet')}
                  className={`p-3 rounded-lg text-sm font-medium transition-all flex flex-col items-center ${
                    walletSource === 'cash_wallet' 
                      ? 'bg-green-500 text-black' 
                      : 'glass text-zinc-400 hover:text-white'
                  }`}
                  data-testid="select-cash-wallet"
                >
                  <span>Cash Wallet</span>
                  <span className="text-xs mt-1">${cashBalance.toFixed(2)}</span>
                  <span className="text-[10px] opacity-70">{usdToBLC(cashBalance)} BLC</span>
                </button>
              </div>
              {walletSource === 'cash_wallet' && (
                <p className="text-xs text-green-400 mt-2">
                  Using Cash Wallet - no need to wait for withdrawal dates!
                </p>
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Participation Amount (USD)</label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="bg-black/50 border-white/10 text-white"
                min={selectedPackage?.min_amount}
                max={selectedPackage?.max_amount}
                data-testid="stake-amount-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Selected Wallet Balance: ${(walletSource === 'deposit_wallet' ? depositBalance : cashBalance).toFixed(2)}
                <span className="text-amber-400 ml-1">({usdToBLC(walletSource === 'deposit_wallet' ? depositBalance : cashBalance)} BLC)</span>
              </p>
            </div>

            {amount && (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm font-medium">Estimated Daily Earning</span>
                </div>
                <p className="text-2xl text-white font-medium">
                  ${(parseFloat(amount) * (selectedPackage?.daily_roi || 0) / 100).toFixed(2)}
                  <span className="text-amber-400 text-sm ml-2">({usdToBLC(parseFloat(amount) * (selectedPackage?.daily_roi || 0) / 100)} BLC)</span>
                </p>
              </div>
            )}

            <Button
              onClick={handleStake}
              disabled={submitting}
              className="w-full gold-btn"
              data-testid="confirm-stake"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                'Confirm Participation'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
